import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import { shiftColorShade, determineFontColor } from 'lib/colorLogic';
import getRadius from 'lib/getRadius';
import messageEnums from 'enums/messages';
import overrideStyle from 'lib/overrideStyle';
import DataRequestStatusBannerContainer from 'presentation/selfService/DataRequestStatusBanner.jsx';
import { getFormattedCurrencyAmount } from 'lib/currencyHelpers';

const COLOR_SHIFT_AMOUNT = 4;

export default function PaymentRequestMessageItem(props) {
  const handleInitiateSecurePayment = useCallback(() => {
    props.onInitiateSecurePayment(props.currency, props.paymentId, props.memo, props.monetaryUnit);
  }, [props.currency, props.paymentId, props.memo, props.monetaryUnit]);

  const handleInitiateSecurePaymentKeyDown = ev => {
    if (ev.key === 'Enter') {
      handleInitiateSecurePayment();
    }
  };

  const handleDecline = useCallback(() => {
    props.onDecline(props.paymentId);
  }, [props.onDecline, props.paymentId]);

  const handleDeclineKeyDown = ev => {
    if (ev.key === 'Enter') {
      handleDecline();
    }
  };

  function renderPaymentActionButtons() {
    if (props.status === messageEnums.SECURE_DATA_REQUEST_STATUSES.REQUESTING) {
      return (
        <ButtonWrapper>
          <TabButtonWrapper
            aria-labelledby="decline-payment"
            data-aid="paymentRequestMessage-declinePayment"
            onClick={handleDecline}
            onKeyDown={handleDeclineKeyDown}
            role="button"
            type="submit"
            tabIndex="0"
          >
            <DeclineButton tabIndex="-1">
              <ButtonText>Decline</ButtonText>
            </DeclineButton>
          </TabButtonWrapper>
          <TabButtonWrapper
            aria-labelledby="initiate-payment"
            data-aid="paymentRequestMessage-initiatePayment"
            onClick={handleInitiateSecurePayment}
            onKeyDown={handleInitiateSecurePaymentKeyDown}
            role="button"
            type="submit"
            tabIndex="0"
          >
            <PayButton tabIndex="-1">
              <ButtonText>Enter Payment</ButtonText>
            </PayButton>
          </TabButtonWrapper>
        </ButtonWrapper>
      );
    }
  }

  return (
    <PaymentRequestMessage>
      <AmountText aria-live="polite">{getFormattedCurrencyAmount(props.currency, props.monetaryUnit)}</AmountText>
      <MemoFlexWrapper>
        <MemoText aria-live="polite">{props.memo}</MemoText>
      </MemoFlexWrapper>
      <DataRequestStatusBannerContainer status={props.status} />
      {renderPaymentActionButtons()}
    </PaymentRequestMessage>
  );
}

PaymentRequestMessageItem.propTypes = {
  currency: PropTypes.string.isRequired,
  monetaryUnit: PropTypes.number.isRequired,
  paymentId: PropTypes.string.isRequired,
  memo: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  onDecline: PropTypes.func.isRequired,
  onInitiateSecurePayment: PropTypes.func.isRequired,
};

const AmountText = overrideStyle('div')`
  font-size: 20px;
  font-weight: bold;
  height: 25px;
  line-height: 25px;
  text-align: center;
  padding: 0px 0px 8px 0px;
  width: 100%;
`;

const Button = overrideStyle('div')`
  display: flex;
  border-radius: ${p => getRadius(p.theme.borderRadius, 4, 8)};
  cursor: pointer;
  justify-content: center;
  flex-direction: column;
  height: 40px;
  outline: none;
  transition: 0.2s ease;
  width: 120px;
`;

const TabButtonWrapper = overrideStyle('div')`
  margin: 0px 10px 16px 10px;

  &:focus {
    box-shadow: inset 0 0 0px 2px #81b5ea;
    outline: solid 1px #81b5ea;
  }
`;

const DeclineButton = overrideStyle(Button)`
  border: 1px solid #E4E4E4;

  &:focus{
    border: 1px solid #a3a3a3;
  }
  &:active{
    border: 1px solid #a3a3a3;
  }
  &:hover{
    border: 1px solid #a3a3a3;
    background-color: #eeeeee;
  }
`;

const PayButton = overrideStyle(Button)`
  background-color: ${p => p.theme.colors.brandPrimary};
  border: 1px solid ${p => p.theme.colors.brandPrimary};
  color: ${p => determineFontColor(p.theme.colors.brandPrimary)};

  &:focus{
    background-color: ${p => shiftColorShade(p.theme.colors.brandPrimary, COLOR_SHIFT_AMOUNT)};
    border: 1px solid${p => shiftColorShade(p.theme.colors.brandPrimary, COLOR_SHIFT_AMOUNT)};
  }
  &:active{
    background-color: ${p => shiftColorShade(p.theme.colors.brandPrimary, -COLOR_SHIFT_AMOUNT)};
    border:1px solid ${p => shiftColorShade(p.theme.colors.brandPrimary, COLOR_SHIFT_AMOUNT)};
  }
  &:hover{
    background-color: ${p => shiftColorShade(p.theme.colors.brandPrimary, COLOR_SHIFT_AMOUNT)};
    border:1px solid ${p => shiftColorShade(p.theme.colors.brandPrimary, COLOR_SHIFT_AMOUNT)};
  }
`;

const ButtonText = overrideStyle('div')`
  cursor: pointer;
  font-family: ${p => p.theme.fontLibrary.primary};
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const ButtonWrapper = overrideStyle('div')`
  display: flex; 
  flex-direction: row;
  justify-content: center;
  width: 100%;
`;

const MemoFlexWrapper = overrideStyle('div')`
  display: flex;
  min-height: 22px;
  justify-content: center;
  margin-bottom: 8px;
`;

const MemoText = overrideStyle('div')`
  color: #929292;
  font-size: 13px;
  height: auto;
  line-height: 14px;
  min-height: 13px;
  overflow-wrap: break-word;
  padding-bottom: 8px;
  text-align: center;
  width: 220px;
`;

const Message = overrideStyle('div')`
  border-radius: ${p => getRadius(p.theme.borderRadius, 8, 16)};
  border: 1px solid #E4E4E4;
  flex: 0 0 auto;
  font-family: ${p => p.theme.fontLibrary.primary};
  padding: 10px 15px;
`;

const PaymentRequestMessage = overrideStyle(Message)`  
  display: flex; 
  flex-direction: column;
  max-height: 250px;
  min-height: 98px;
  justify-content: center;
  padding: 16px 0px;
  margin: 33px;
  align-self: center;
  width 298px;
`;
